import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    type MoneyMapRuleGroup,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { orderBy } from 'lodash';
import Svg, { Path } from 'react-native-svg';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Icon,
    redactPrivate,
    TargetIcon,
    useThemeColors,
    XStack,
    YStack,
} from 'smoothie';

import { getDisplayAccountsForMoneyMapRule } from '../utils/moneyMapAccountUtils';
import {
    getSyntheticGoalPriorityFromActual,
    prioritySortRulesAndGroups,
} from '../utils/moneyMapRuleUtils';
import { MoneyMapAccountIcon } from './MoneyMapAccountIcon';
import {
    MoneyMapRulePercentage,
    MoneyMapRulePriority,
} from './MoneyMapAccountRule';
import {
    getMoneyMapAccountSubtitle,
    type MoneyMapAccountHolder,
    MoneyMapAccountNameWithTooltip,
} from './MoneyMapAccountRuleRedesign';

interface MoneyMapGoalExpandedRedesignProps {
    moneyMap: MoneyMapWithRulesAndAccounts;
    rules: MoneyMapRuleWithAccountDetails[];
    accountsById: Record<string, MoneyMapAccountWithAccountDetails>;
    accountHolders?: MoneyMapAccountHolder[];
}

const getAccountsForRule = (
    rule: MoneyMapRuleWithAccountDetails,
    accountsById: Record<string, MoneyMapAccountWithAccountDetails>,
) => {
    const { primaryAccount, secondaryAccount } =
        getDisplayAccountsForMoneyMapRule(rule, accountsById);

    return {
        primaryAccount: primaryAccount ?? rule.moneyMapAccount,
        secondaryAccount,
    };
};

const GoalRuleTarget = ({ rule }: { rule: MoneyMapRuleWithAccountDetails }) => {
    if (rule.targetBalanceCents == null || rule.targetBalanceCents <= 0) {
        return null;
    }

    return (
        <XStack gap={4} alignItems="center" {...redactPrivate()}>
            <Icon icon={TargetIcon} size={16} />
            <BodyMediumRegular>
                {centsToFormattedDollars(rule.targetBalanceCents, false)}
            </BodyMediumRegular>
        </XStack>
    );
};

const getGoalRuleConstraints = (
    rule: MoneyMapRuleWithAccountDetails,
): string[] => {
    return [
        rule.monthlyContributionMaxCents != null &&
        rule.monthlyContributionMaxCents > 0
            ? `Max ${centsToFormattedDollars(rule.monthlyContributionMaxCents, false)}/mo`
            : null,
        rule.yearlyContributionMaxCents != null &&
        rule.yearlyContributionMaxCents > 0
            ? `Max ${centsToFormattedDollars(rule.yearlyContributionMaxCents, false)}/yr`
            : null,
        rule.moneyMapAccount?.hasRetirementContributionLimit === true
            ? 'Until annual IRA limit'
            : null,
    ].filter((constraint): constraint is string => constraint != null);
};

const GoalRuleConstraints = ({ constraints }: { constraints: string[] }) => {
    return (
        <YStack gap={2} {...redactPrivate()}>
            {constraints.map((constraint) => (
                <BodySmallRegular key={constraint} color="$gray1000">
                    {constraint}
                </BodySmallRegular>
            ))}
        </YStack>
    );
};

const RailConnector = ({
    arrow = false,
    dashed = false,
    startInset = 0,
}: {
    arrow?: boolean;
    dashed?: boolean;
    startInset?: number;
}) => {
    const { getColor } = useThemeColors();
    const stroke = getColor('$petalDarkest');

    if (arrow) {
        return (
            <Svg width={32} height={40} viewBox="0 0 32 40">
                <Path
                    d={`M16 ${startInset}V37`}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={1}
                    strokeDasharray={dashed ? '3 3' : undefined}
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                />
                <Path
                    d="M12 33L16 37L20 33"
                    fill="none"
                    stroke={stroke}
                    strokeWidth={1}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </Svg>
        );
    }

    return (
        <YStack flex={1} paddingTop={startInset}>
            <Svg
                width={32}
                height="100%"
                viewBox="0 0 32 1"
                preserveAspectRatio="none"
            >
                <Path
                    d="M16 0V1"
                    fill="none"
                    stroke={stroke}
                    strokeWidth={1}
                    strokeDasharray={dashed ? '3 3' : undefined}
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                />
            </Svg>
        </YStack>
    );
};

const ExpandedGoalRule = ({
    rule,
    accountsById,
    accountHolders,
}: {
    rule: MoneyMapRuleWithAccountDetails;
    accountsById: Record<string, MoneyMapAccountWithAccountDetails>;
    accountHolders?: MoneyMapAccountHolder[];
}) => {
    const { primaryAccount, secondaryAccount } = getAccountsForRule(
        rule,
        accountsById,
    );
    const constraints = getGoalRuleConstraints(rule);

    return (
        <YStack padding={12} borderRadius={16} backgroundColor="$white">
            <XStack gap={12} minHeight={32} alignItems="center">
                <YStack width={32} alignItems="center">
                    <MoneyMapRulePercentage percentage={rule.percentage ?? 1} />
                </YStack>
                <XStack flex={1} gap={12} alignItems="flex-start">
                    <BodyMediumRegular flex={1}>
                        {rule.description}
                    </BodyMediumRegular>
                    <GoalRuleTarget rule={rule} />
                </XStack>
            </XStack>
            {constraints.length > 0 && (
                <XStack gap={12} alignItems="stretch">
                    <YStack width={32} minHeight={24} alignItems="center">
                        <RailConnector startInset={5} />
                    </YStack>
                    <YStack flex={1}>
                        <GoalRuleConstraints constraints={constraints} />
                    </YStack>
                </XStack>
            )}
            <XStack gap={12} height={40} alignItems="stretch">
                <YStack width={32} alignItems="center">
                    <RailConnector
                        arrow
                        startInset={constraints.length > 0 ? 0 : 5}
                    />
                </YStack>
                <YStack flex={1} />
            </XStack>
            <XStack gap={12} minHeight={40} marginTop={-5} alignItems="center">
                <YStack width={32} alignItems="center">
                    <MoneyMapAccountIcon
                        category={MoneyMapCategory.GOALS}
                        account={primaryAccount}
                    />
                </YStack>
                <YStack flex={1} gap={2}>
                    <MoneyMapAccountNameWithTooltip
                        name={primaryAccount.accountDetails.name}
                        account={primaryAccount}
                        accountHolders={accountHolders}
                    />
                    <BodySmallRegular color="$gray1000">
                        {getMoneyMapAccountSubtitle(primaryAccount)}
                    </BodySmallRegular>
                </YStack>
            </XStack>
            {secondaryAccount != null && (
                <>
                    <XStack gap={12} height={40} alignItems="stretch">
                        <YStack width={32} alignItems="center">
                            <RailConnector arrow dashed startInset={5} />
                        </YStack>
                        <YStack flex={1} />
                    </XStack>
                    <XStack
                        gap={12}
                        minHeight={40}
                        marginTop={-5}
                        alignItems="center"
                    >
                        <YStack width={32} alignItems="center">
                            <MoneyMapAccountIcon
                                category={MoneyMapCategory.GOALS}
                                account={secondaryAccount}
                            />
                        </YStack>
                        <YStack flex={1} gap={2}>
                            <MoneyMapAccountNameWithTooltip
                                name={secondaryAccount.accountDetails.name}
                                account={secondaryAccount}
                                accountHolders={accountHolders}
                            />
                            <BodySmallRegular color="$gray1000">
                                {getMoneyMapAccountSubtitle(secondaryAccount)}
                            </BodySmallRegular>
                        </YStack>
                    </XStack>
                </>
            )}
        </YStack>
    );
};

const ExpandedGoalGroup = ({
    group,
    rules,
    moneyMap,
    accountsById,
    accountHolders,
}: {
    group: MoneyMapRuleGroup;
    rules: MoneyMapRuleWithAccountDetails[];
    moneyMap: MoneyMapWithRulesAndAccounts;
    accountsById: Record<string, MoneyMapAccountWithAccountDetails>;
    accountHolders?: MoneyMapAccountHolder[];
}) => {
    return (
        <YStack gap={12} padding={16}>
            <XStack gap={12} alignItems="center">
                <MoneyMapRulePriority
                    priority={getSyntheticGoalPriorityFromActual(
                        group.priority,
                        moneyMap,
                    )}
                />
                <BodyMediumRegular flex={1}>{group.name}</BodyMediumRegular>
            </XStack>
            <YStack gap={8}>
                {orderBy(rules, 'priority').map((rule) => (
                    <ExpandedGoalRule
                        key={rule.id}
                        rule={rule}
                        accountsById={accountsById}
                        accountHolders={accountHolders}
                    />
                ))}
            </YStack>
        </YStack>
    );
};

export const MoneyMapGoalExpandedRedesign = ({
    moneyMap,
    rules,
    accountsById,
    accountHolders,
}: MoneyMapGoalExpandedRedesignProps) => {
    const groupsWithRules = moneyMap.moneyMapRuleGroups.filter((group) =>
        rules.some((rule) => rule.moneyMapRuleGroupId === group.id),
    );
    const topLevelItems = prioritySortRulesAndGroups(rules, groupsWithRules);

    return (
        <YStack gap={8} backgroundColor="$gray100">
            {topLevelItems.map((item) => {
                if (item.entityType === 'group') {
                    const group = item as MoneyMapRuleGroup;
                    return (
                        <ExpandedGoalGroup
                            key={group.id}
                            group={group}
                            rules={rules.filter(
                                (rule) => rule.moneyMapRuleGroupId === group.id,
                            )}
                            moneyMap={moneyMap}
                            accountsById={accountsById}
                            accountHolders={accountHolders}
                        />
                    );
                }

                const rule = item as MoneyMapRuleWithAccountDetails;
                return (
                    <YStack key={rule.id} padding={16} gap={12}>
                        <MoneyMapRulePriority
                            priority={getSyntheticGoalPriorityFromActual(
                                rule.priority,
                                moneyMap,
                            )}
                        />
                        <ExpandedGoalRule
                            rule={rule}
                            accountsById={accountsById}
                            accountHolders={accountHolders}
                        />
                    </YStack>
                );
            })}
        </YStack>
    );
};
